/**
 * Every published event, on kgob.com, reachable without a token — the list the app agent found missing.
 *
 * Plain `<a>` for reading, the gate only for RSVP (WHO-OWNS-WHICH-URL.md §2). Rendered by the static build as
 * well as in the browser, so a crawler that never runs JavaScript still finds every event page.
 */
import { Badge } from "./Hero";

const typeStyle = (hue) =>
  hue === undefined || hue === null ? undefined : {
    "--type": `oklch(0.52 0.13 ${hue})`,
    "--type-text": `oklch(0.40 0.13 ${hue})`,
    "--type-tint": `oklch(0.52 0.13 ${hue} / 0.12)`,
  };

function Row({ r }) {
  return (
    <li className="ev-list-row" style={typeStyle(r.type?.hue)} data-testid={`event-list-row-${r.slug}`}>
      <div className="ev-list-main">
        {r.type && <span className="ev-type-tag">{r.type.name}</span>}
        <h3 className="ev-list-title">
          <a href={r.href} data-testid={`event-list-link-${r.slug}`}>{r.title}</a>
        </h3>
        {r.tagline && <p className="ev-list-tagline">{r.tagline}</p>}
        {r.next ? (
          <p className="ev-list-meta">
            <time dateTime={r.next.starts_at}>{r.next.when}</time>
            {r.next.place && <span className="ev-list-where">{r.next.place}</span>}
            {r.dates > 1 && <span className="ev-list-more">{r.dates} dates</span>}
          </p>
        ) : (
          <p className="ev-list-meta">Every date has passed</p>
        )}
      </div>
      <div className="ev-list-side">
        {r.next && <Badge badge={r.next.badge} />}
        {r.rsvp && (
          <a className="ev-btn ev-btn-gold" href={r.rsvp.href} data-rsvp={r.rsvp.event_session_id}
             data-testid={`event-list-rsvp-${r.slug}`}>{r.rsvp.label} <span aria-hidden="true">→</span></a>
        )}
      </div>
    </li>
  );
}

export function EventList({ list }) {
  // Their ask (2026-09-15): #ev-list must be reachable. It used to disappear entirely when the list was empty,
  // so an anchor to it went nowhere and every kind of empty looked identical — including our own defect.
  if (!list) return null;
  return (
    <>
      <section className="ev-wrap ev-section" id="ev-list" aria-labelledby="ev-list-h" data-testid="event-list">
        <p className="ev-eyebrow">All events</p>
        <h2 id="ev-list-h">What&rsquo;s coming up</h2>
        {list.upcoming.length ? (
          <ul className="ev-list">{(list.upcoming || []).map((r) => <Row key={r.slug} r={r} />)}</ul>
        ) : (
          list.defect ? (
            /* Never "no events": they sent events and we kept none, which is our fault, not an empty calendar. */
            <p data-testid="event-list-defect">
              We are having trouble showing the calendar just now. It is being looked at — please call or email
              us and we will tell you what is coming up.
            </p>
          ) : list.stale ? (
            <p data-testid="event-list-stale">
              We could not reach the calendar just now. Please call or email us and we will tell you what is
              coming up.
            </p>
          ) : (
            <p data-testid="event-list-empty">No dates are on the calendar just now. New events are added most months — call or email us and we will let you know.</p>
          )
        )}
      </section>
      {list.past.length > 0 && (
        <section className="ev-wrap ev-section ev-list-past" id="ev-past" aria-labelledby="ev-past-h" data-testid="event-list-past">
          <p className="ev-eyebrow">Recently held</p>
          <h2 id="ev-past-h">{list.windowDays ? `Finished in the last ${list.windowDays} days` : "Events that have finished"}</h2>
          <ul className="ev-list">{(list.past || []).map((r) => <Row key={r.slug} r={r} />)}</ul>
        </section>
      )}
    </>
  );
}
